import { Box } from '@material-ui/core';
import { BorrowerOnboardingDetails, OnboardingFulfilledBy } from '@nemuru-eng/js-api-client';

import { DocumentIdEditDialog } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/components/DocumentIdEditDialog';
import { DocumentIdForm } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/components/DocumentIdForm';
import { DocumentIdImages } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/components/DocumentIdImages';
import { KycDataDialog } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/components/KycDataDialog/KycDataDialog';
import { KycFulfilledByMerchant } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/components/KycFulfilledByMerchant';
import { KycResult } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/components/KycResult';
import { NieDataForm } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/components/NieDataForm';
import { useKYCDocumentId } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/hooks/useKYCDocumentId';
import { useUpdateNieData } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/hooks/useUpdateNieData';
import { useBorrowerKYC } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/hooks/useBorrowerKYC';
import { useUpdateBorrowerData } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/hooks/useUpdateBorrowerContract';
import { ActionDialog } from '@/components/shared/components/ActionDialog';
import { Font } from '@/components/shared/components/Font';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { SubSectionWrapper } from '@/components/shared/components/SubSectionWrapper';
import { Text } from '@/components/shared/components/Text';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { PendingDataType } from '@/config/types';
import { usePageContext } from '@/contexts/PageContext/PageContext';

interface DocumentIdSectionProps {
  loanId: string;
  onboardingDetails: BorrowerOnboardingDetails;
  editModeAllowed?: boolean;
}

export const DocumentIdSection = ({ loanId, onboardingDetails, editModeAllowed = false }: DocumentIdSectionProps) => {
  const { getTextIdTranslation } = useTranslation();
  const { opened, toggleDialog, setOpened } = useDialog();
  const { filteredKyc } = useBorrowerKYC({
    externalId: loanId,
    onboardingDetails,
  });
  const { updateBorrowerContract, requestLoading: updateBorrowerContractLoading } = useUpdateBorrowerData({
    loanId,
    onboardingDetails,
  });

  const nieData = onboardingDetails.nieData ?? null;
  const showNieDataFields = nieData != null;

  const { patchNieData, requestLoading: updateNieDataLoading } = useUpdateNieData({
    loanId,
    onboardingDetailsId: onboardingDetails.id,
  });

  const {
    state: { applicationParticipant },
  } = usePageContext();

  const {
    dialogAction,
    setDialogAction,
    toggleEditModeDialog,
    onSubmitKycRepeatEmail,
    documentIdActionsOptions,
    loan,
    loading,
    nieDataRequired,
  } = useKYCDocumentId({
    loanId,
    toggleDialog,
    setOpened,
    kyc: filteredKyc,
    editModeAllowed,
    onboardingDetails,
  });

  const documentId = 'userDetails' in onboardingDetails && onboardingDetails?.userDetails?.documentId;
  const borrowerId = 'borrowerId' in onboardingDetails ? onboardingDetails.borrowerId : null;
  const requestLoading = loading || updateBorrowerContractLoading || updateNieDataLoading;
  const showBirthDateField = true;

  if (!loan) return <></>;

  return (
    <SubSectionWrapper
      title={'DOCUMENT_ID'}
      leftComponent={
        onboardingDetails.fulfilledBy === OnboardingFulfilledBy.MERCHANT_POS ? (
          <KycFulfilledByMerchant />
        ) : filteredKyc && filteredKyc?.length > 0 ? (
          <KycResult kyc={filteredKyc[0]} />
        ) : (
          <></>
        )
      }
      specificActionsOptionsArray={toggleEditModeDialog ? documentIdActionsOptions : undefined}
    >
      {documentId ? (
        <>
          <Box style={{ paddingBlock: '24px' }}>
            <DocumentIdImages
              documentType={documentId.documentType}
              loanId={loan.id}
              borrowerId={borrowerId}
              key={borrowerId}
            />
          </Box>
          <DocumentIdForm
            documentId={documentId}
            fullWidth={false}
            showAddressFields
            showBirthDateField={showBirthDateField}
          />
          {showNieDataFields && (
            <Box pt={3}>
              <Box pb={1}>
                <Font variant={'subtitle2'} color={'secondary'}>
                  <Text strong tId={'NIE_DATA'} />
                </Font>
              </Box>
              <NieDataForm documentType={documentId.documentType} nieData={nieData} fullWidth={false} />
            </Box>
          )}
          {opened && dialogAction === DocumentIdAction.EDIT_DIALOG && (
            <DocumentIdEditDialog
              documentId={documentId}
              opened={opened}
              setOpened={setOpened}
              toggleDialog={toggleDialog}
              patchLoanUserDetails={updateBorrowerContract}
              patchNieData={patchNieData}
              loading={requestLoading}
              showBirthDateField={showBirthDateField}
              nieData={nieData}
              nieDataRequired={nieDataRequired}
            />
          )}
        </>
      ) : (
        <PendingDataWarning type={PendingDataType.BORROWER_ONBOARDING} />
      )}
      {filteredKyc && opened && dialogAction === DocumentIdAction.CHECK_KYC_DATA && (
        <KycDataDialog
          opened={opened}
          setOpened={setOpened}
          setActionDialogParams={() => {
            setDialogAction(undefined);
          }}
          kyc={filteredKyc}
        />
      )}
      {documentId && opened && dialogAction === DocumentIdAction.SEND_REPEAT_KYC_EMAIL && (
        <ActionDialog
          Title={<Text tId={'SEND_REPEAT_KYC_EMAIL'} />}
          Description={
            <Text
              tId={'SEND_REPEAT_KYC_EMAIL_TEXT'}
              dynamicDataTexts={[
                `${documentId?.fullName.firstName} ${documentId?.fullName.lastName}`,
                getTextIdTranslation(applicationParticipant.role) as string,
              ]}
            />
          }
          MainButton={<Text tId={'SEND_REPEAT_KYC_EMAIL_BTN'} />}
          opened={opened}
          setOpened={setOpened}
          onSubmit={onSubmitKycRepeatEmail}
          handleClose={() => {
            setDialogAction(undefined);
            setOpened(false);
          }}
          loading={loading}
        />
      )}
    </SubSectionWrapper>
  );
};

export enum DocumentIdAction {
  EDIT_DIALOG = 'edit_dialog',
  CHECK_KYC_DATA = 'check_kyc_data',
  SEND_REPEAT_KYC_EMAIL = 'send_repeat_kyc_email',
}
